import { useEffect, useRef, useState } from 'react';
import { siteData } from '../data/mock';

export const AboutSection = () => {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="about"
      ref={sectionRef}
      className="relative py-20 md:py-40"
      style={{ background: '#0a0c0f' }}
    >
      {/* Subtle separator line at top */}
      <div className="absolute top-0 left-5 right-5 md:left-10 md:right-10 h-px bg-gradient-to-r from-transparent via-[rgba(0,212,255,0.15)] to-transparent" />

      <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
        {/* Top row: Label + Heading */}
        <div
          className={`mb-12 md:mb-20 transition-all duration-1000 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="font-montserrat text-xs tracking-[0.3em] uppercase text-[#00d4ff] mb-4">
            The Author
          </p>
          <h2 className="font-montserrat font-800 text-3xl md:text-5xl uppercase text-[#e8e6e1] leading-[0.9]">
            About{' '}
            <span className="text-[#c4a962]">GS Rylan</span>
          </h2>
        </div>

        {/* Main content: Photo + Bio */}
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-20 items-start">
          {/* Left: Author Photo */}
          <div
            className={`lg:w-[38%] flex-shrink-0 transition-all duration-1000 delay-200 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="relative">
              {/* Subtle glow behind photo */}
              <div
                className="absolute -inset-4 opacity-10 blur-2xl rounded-sm"
                style={{
                  background: 'radial-gradient(ellipse, #00d4ff, transparent 70%)',
                }}
              />

              {/* Photo container */}
              <div className="relative overflow-hidden">
                {/* Thin accent border on left */}
                <div className="absolute top-0 left-0 w-[2px] h-full bg-gradient-to-b from-[#00d4ff] via-[#00d4ff]/40 to-transparent z-10" />

                <img
                  src={siteData.images.authorPhoto}
                  alt="G.S. Rylan"
                  className="w-full object-cover relative z-[1]"
                  style={{
                    filter: 'brightness(0.9) contrast(1.05)',
                    maxHeight: '550px',
                  }}
                />

                {/* Bottom fade overlay */}
                <div
                  className="absolute bottom-0 left-0 right-0 h-24 z-[2]"
                  style={{
                    background: 'linear-gradient(to top, #0a0c0f, transparent)',
                  }}
                />
              </div>

              {/* Name caption below photo */}
              <div className="mt-5 flex items-center gap-3">
                <div className="w-8 h-px bg-[#c4a962]/50" />
                <p className="font-montserrat text-xs tracking-[0.2em] uppercase text-[#888680]">
                  G.S. Rylan
                </p>
              </div>
            </div>
          </div>

          {/* Right: Bio Text */}
          <div className="lg:w-[62%]">
            <div className="space-y-6">
              {siteData.author.bio.map((paragraph, i) => {
                const isHighlight = i === 2 || i === 3;
                return (
                  <p
                    key={i}
                    className={`transition-all duration-1000 ${
                      visible
                        ? 'opacity-100 translate-y-0'
                        : 'opacity-0 translate-y-6'
                    } ${
                      isHighlight && i === 3
                        ? 'font-montserrat font-700 text-xl md:text-2xl text-[#00d4ff] italic pl-6 border-l-2 border-[#00d4ff]/30'
                        : isHighlight
                        ? 'font-cardo text-lg text-[#888680] italic'
                        : 'font-cardo text-lg text-[#b0ada6] leading-relaxed'
                    }`}
                    style={{ transitionDelay: `${300 + i * 150}ms` }}
                  >
                    {paragraph}
                  </p>
                );
              })}
            </div>

            {/* Extended bio */}
            <div
              className={`mt-12 pt-8 border-t border-[rgba(255,255,255,0.05)] transition-all duration-1000 delay-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <p className="font-cardo text-base text-[#666] leading-relaxed max-w-2xl">
                {siteData.author.fullBio}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
